// frontend/src/samkalp-faculty/SamkalpFaculty.js
//
// Root gate for the Samkalp faculty entry point. If already authenticated
// as classroom staff (JWT app_context === 'classroom'), render the Faculty
// Dashboard (Live Sessions + Teaching Copilot, embedded in the same
// workspace). Otherwise render the branded login.
//
// The `loggedIn` state (rather than deriving isFaculty from the JWT alone,
// unconditionally) exists solely so SamkalpFacultyLoginPage's onLogin
// callback can force this gate to re-render right after localStorage gets
// a fresh token — mirrors SamkalpAdmin.js's identical gate pattern. Without
// it, there'd be no React-visible signal that login just succeeded, and
// the login page would have to navigate() to some other route entirely
// (the earlier bug: it sent faculty to the generic /tenant/:slug/copilot
// page instead of back through this gate to SamkalpFacultyDashboard).
//
// Redux hydration: SamkalpFacultyLoginPage dispatches setUser() at login
// time, but that's in-memory only — Redux resets to its initialState on
// every full page load/refresh, while the JWT in localStorage persists.
// ProtectedRoute (used by the embedded Teaching Copilot's own API-auth
// checks elsewhere in the app) reads Redux state.user, not the JWT
// directly, so without re-dispatching here, a refreshed tab would look
// logged-in (this gate reads the JWT fine) but bounce to a login screen
// the moment anything checks Redux — exactly the "opens a new login"
// symptom. Re-hydrating from the faculty's own profile on every mount
// keeps Redux and the JWT in sync regardless of how the tab got here.
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import SamkalpFacultyLoginPage from './SamkalpFacultyLoginPage';
import SamkalpFacultyDashboard from './SamkalpFacultyDashboard';
import { setUser } from '../redux/slices/userSlice';
import { getCopilotProfile } from '../api/api';

const decodeToken = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
};

const handleLogout = () => {
    localStorage.removeItem('access_token');
    window.location.href = '/samkalp-ias/faculty';
};

const SamkalpFaculty = () => {
    const dispatch = useDispatch();
    const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem('access_token'));
    const token = localStorage.getItem('access_token');
    const payload = token ? decodeToken(token) : null;
    const isFaculty = loggedIn && payload?.app_context === 'classroom' && payload?.tenant_slug;

    useEffect(() => {
        if (!isFaculty) return;
        getCopilotProfile(payload.tenant_slug)
            .then(profile => dispatch(setUser({ ...profile, app_context: 'classroom', tenant_slug: payload.tenant_slug })))
            .catch(() => dispatch(setUser({ app_context: 'classroom', tenant_slug: payload.tenant_slug })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFaculty]);

    if (isFaculty) {
        return <SamkalpFacultyDashboard slug={payload.tenant_slug} onLogout={handleLogout} />;
    }

    return <SamkalpFacultyLoginPage onLogin={() => setLoggedIn(true)} />;
};

export default SamkalpFaculty;
